import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import React, { useEffect, useState } from "react";
import { Button, Modal } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { invalidateQueries } from "../../../common/dataUtils";
import { formatErrorMessage } from "../../../common/utils";
import { upsertFormDropdown } from "../../../services/formDropdownsApiService";
import { showToaster } from "../../../services/toastService";
import { IFormDropdown } from "./types";

interface IProps {
  show: boolean;
  formDropdown: IFormDropdown;
  type: string;
  onHide: (e?: any) => void;
  onSaved: (formDropdown: IFormDropdown) => void;
}

const FormDropdownsCreateDialog: React.FC<IProps> = (props) => {
  const [showModal, setShowModal] = useState(false);
  const [title, setTitle] = useState("");

  const {
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { errors, isDirty },
  } = useForm();

  useEffect(() => {
    reset(props.formDropdown);
    let _title = props.formDropdown?.id ? "Edit " : "Create ";
    setTitle(_title);
    setShowModal(props.show);
  }, [props.show]);

  const { mutateAsync } = useMutation({
    mutationFn: upsertFormDropdown,
  });
  const queryClient = useQueryClient();

  function handleCloseDialog(event: any = null): void {
    setShowModal(false);
    props.onHide();
  }

  async function handleSaveFormDropdown(data: any) {
    if (!isEmpty(errors) || !isDirty) return;

    try {
      const resp = await mutateAsync(data);
      handleCloseDialog();
      invalidateQueries(queryClient, ["form-dropdowns"]);
      showToaster("dropdown item saved: " + resp.data?.option_text);
    } catch (err: any) {
      showToaster(
        "Error saving item: " + formatErrorMessage(err.message),
        "error",
      );
    }
  }

  return (
    <Modal
      show={showModal}
      backdrop="static"
      keyboard={false}
      onHide={handleCloseDialog}
      dialogClassName="app-form-dialog"
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form className="row p-3 g-3 align-items-center">
          <div className="mb-3 row">
            <label
              htmlFor="option_text"
              className="col-sm-3 col-form-label text-end fw-semibold"
            >
              Label
            </label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                placeholder="Each"
                {...register("option_text", { required: true, minLength: 2 })}
              />
              {errors.option_text && errors.option_text.type === "required" && (
                <span className="text-danger">This is required</span>
              )}
              {errors.option_text &&
                errors.option_text.type === "minLength" && (
                  <span className="text-danger">Min length 2</span>
                )}
            </div>
          </div>
          <div className="mb-3 row">
            <label
              htmlFor="option_value"
              className="col-sm-3 col-form-label text-end fw-semibold"
            >
              Value
            </label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                placeholder="each"
                {...register("option_value", { required: true, minLength: 2 })}
              />
              {errors.option_value &&
                errors.option_value.type === "required" && (
                  <span className="text-danger">This is required</span>
                )}
              {errors.option_value &&
                errors.option_value.type === "minLength" && (
                  <span className="text-danger">Min length 2</span>
                )}
            </div>
          </div>
          <div className="mb-3 row">
            <label
              htmlFor="dropdown_name"
              className="col-sm-3 col-form-label text-end fw-semibold"
            >
              Dropdown Name
            </label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                placeholder="job-status"
                defaultValue={props.type}
                {...register("dropdown_name", { required: true, minLength: 2 })}
              />
              {errors.dropdown_name &&
                errors.dropdown_name.type === "required" && (
                  <span className="text-danger">This is required</span>
                )}
              {errors.dropdown_name &&
                errors.dropdown_name.type === "minLength" && (
                  <span className="text-danger">Min length 2</span>
                )}
            </div>
          </div>
          <div className="mb-3 row">
            <label
              htmlFor="is_active"
              className="col-sm-3 col-form-label text-end fw-semibold"
            >
              Status
            </label>
            <div className="col-sm-4">
              <select className="form-select" {...register("is_active")}>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>
          </div>
          <div className="mb-3 row">
            <label
              htmlFor="metadata"
              className="col-sm-3 col-form-label text-end fw-semibold"
            >
              Rate
            </label>
            <div className="col-sm-8">
              <input
                type="text"
                className="form-control"
                placeholder="1"
                {...register("metadata")}
              />
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleCloseDialog}>
          Cancel
        </Button>
        <Button
          variant="success"
          onClick={handleSubmit(handleSaveFormDropdown)}
          disabled={!isDirty}
        >
          Save
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default FormDropdownsCreateDialog;
